import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listInquiries, deleteInquiry } from "@/lib/admin.functions";
import { toast } from "sonner";
import { Loader2, Trash2, Pencil } from "lucide-react";
import { PageHeader } from "@/components/admin/shared";

export const Route = createFileRoute("/admin/inquiries/")({
  component: InquiriesListPage,
});

type Inquiry = {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  event_date: string | null;
  location: string | null;
  guests: number | null;
  message: string | null;
  products: { slug: string; name: string; image: string; quantity?: number }[];
  status: string;
  created_at: string;
};

const STATUS_LABELS: Record<string, string> = {
  new: "Ny",
  in_progress: "Pågående",
  done: "Klar",
  archived: "Arkiverad",
};
const STATUS_STYLES: Record<string, string> = {
  new: "bg-accent text-accent-foreground",
  in_progress: "bg-primary text-primary-foreground",
  done: "bg-secondary text-foreground",
  archived: "bg-muted text-muted-foreground",
};

function InquiriesListPage() {
  const list = useServerFn(listInquiries);
  const del = useServerFn(deleteInquiry);
  const [items, setItems] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");

  const reload = async () => {
    setLoading(true);
    try {
      setItems((await list({})) as unknown as Inquiry[]);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    reload();
  }, []);

  const remove = async (id: string) => {
    if (!confirm("Ta bort förfrågan?")) return;
    try {
      await del({ data: { id } });
      toast.success("Borttagen");
      setItems((p) => p.filter((i) => i.id !== id));
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  if (loading) return <Loader2 className="h-5 w-5 animate-spin" />;

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);
  const counts = items.reduce<Record<string, number>>((acc, i) => {
    acc[i.status] = (acc[i.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <PageHeader title={`Förfrågningar (${items.length})`} />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["all", "new", "in_progress", "done", "archived"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
              filter === s
                ? "bg-primary text-primary-foreground"
                : "bg-secondary hover:bg-secondary/70"
            }`}
          >
            {s === "all"
              ? `Alla (${items.length})`
              : `${STATUS_LABELS[s]} (${counts[s] ?? 0})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="text-sm text-muted-foreground">Inga förfrågningar.</p>
      )}

      <div className="space-y-2">
        {filtered.map((i) => (
          <div
            key={i.id}
            className="rounded-xl border border-border bg-card p-4 flex items-center gap-3 flex-wrap"
          >
            <span
              className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                STATUS_STYLES[i.status] ?? "bg-secondary"
              }`}
            >
              {STATUS_LABELS[i.status] ?? i.status}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{i.name}</div>
              <div className="text-xs text-muted-foreground truncate">
                {i.email}
                {i.products.length > 0 && ` · ${i.products.length} produkter`}
              </div>
            </div>
            <div className="hidden sm:block text-xs text-muted-foreground whitespace-nowrap">
              {new Date(i.created_at).toLocaleDateString("sv-SE")}
            </div>
            <Link
              to="/admin/inquiries/$id"
              params={{ id: i.id }}
              className="p-2.5 rounded-md bg-secondary hover:bg-secondary/70 min-w-[44px] min-h-[44px] inline-flex items-center justify-center"
              title="Öppna"
              aria-label="Öppna förfrågan"
            >
              <Pencil className="h-4 w-4" />
            </Link>
            <button
              onClick={() => remove(i.id)}
              className="p-2.5 rounded-md bg-destructive text-destructive-foreground min-w-[44px] min-h-[44px] inline-flex items-center justify-center"
              aria-label="Ta bort förfrågan"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
